import _defineProperty from "@babel/runtime/helpers/defineProperty";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

import { rem } from 'polished';
import range from 'lodash/range';
import * as icons from "./defaultIcons";
var namedColors = {
  white: '#FFFFFF',
  brightSun: '#FBCB3B',
  red: '#E00E00',
  maroon: '#870000',
  blue: '#8DE2E0',
  lightBlue: '#E7F7F7',
  bayBlue15: '#E1FAF9',
  bayBlue30: '#BFF4F2',
  bayBlue65: '#7ED2D0',
  orange: '#ED710B',
  lightOrange: '#FCEBCD',
  green: '#26A701',
  lightGreen: '#D5F2CA',
  darkGreen: '#0F7401'
};
var brand = {
  primary: '#E40000',
  secondary: '#8DE2E0',
  tertiary: '#D20000',
  quaternary: '#F9F3E9'
};
var greys = {
  charcoal: '#323232',
  steel: '#666666',
  whisper: '#E6E6E6',
  alto: '#D9D9D9',
  porcelain: '#F6F6F6',
  dusty: '#999999',
  mineShaft: '#222222'
};
var ui = {
  error: namedColors.orange,
  errorBackground: namedColors.lightOrange,
  info: namedColors.blue,
  infoBackground: namedColors.lightBlue,
  link: namedColors.red,
  linkHover: namedColors.maroon,
  success: namedColors.green,
  successDark: namedColors.darkGreen,
  successBackground: namedColors.lightGreen,
  focusColor: '#0E62C9',
  badgeText: greys.charcoal,
  badgeBackground: namedColors.blue
};

var colors = _objectSpread({
  brand: brand,
  greys: greys,
  ui: ui
}, namedColors);

var alertStyles = {
  "default": {
    bg: 'greys.porcelain'
  },
  error: {
    bg: 'ui.errorBackground',
    icon: {
      name: 'warning',
      color: 'ui.error'
    }
  },
  info: {
    bg: 'ui.infoBackground',
    icon: {
      name: 'info',
      color: 'greys.charcoal'
    }
  },
  success: {
    bg: 'ui.successBackground',
    icon: {
      name: 'checkCircle',
      color: 'ui.success'
    }
  }
};
var borders = [0, '1px solid', '2px solid', '3px solid'];
var breakpoints = ['48rem', '68.75rem', '80rem'];
var maxWidths = {
  "default": rem('1260px')
};
var buttons = {
  "default": {
    color: 'white',
    backgroundColor: colors.greys.charcoal
  },
  primary: {
    color: 'white',
    backgroundColor: colors.brand.primary
  }
};
var buttonGroup = {
  "default": {
    color: colors.greys.charcoal,
    borderColor: colors.blue
  },
  primary: {
    color: colors.brand.primary,
    borderColor: colors.brand.primary
  }
};
var errorMessage = {
  backgroundColor: colors.ui.errorBackground
};
var fontFamily = 'Ciutadella, sans-serif';
var fontSizes = {
  xs: rem('14px'),
  sm: rem('15px'),
  base: rem('16px'),
  md: rem('18px'),
  lg: rem('20px'),
  xl: rem('22px'),
  '2xl': rem('28px'),
  '3xl': rem('36px'),
  '4xl': rem('48px')
};
var fontWeights = {
  normal: 400,
  bold: 600
};
var gutters = {
  "default": 3
};
var inputStyles = {
  "default": {
    borderColor: greys.alto
  },
  error: {
    borderColor: ui.error
  },
  focused: {
    borderColor: brand.secondary
  }
};
var letterSpacings = {
  normal: '0',
  wide: '0.125em'
};
var lineHeights = {
  tight: 1,
  normal: 1.35,
  loose: 1.5
};
var mediaCounter = {
  dark: {
    color: colors.white,
    backgroundColor: colors.greys.mineShaft,
    opacity: 0.6,
    textTransform: 'uppercase',
    letterSpacing: '1.5px'
  },
  light: {
    color: colors.greys.charcoal,
    backgroundColor: colors.white,
    opacity: 1,
    textTransform: 'uppercase',
    letterSpacing: '1.5px'
  }
};
var mediaQueries = breakpoints.map(function (n) {
  return "@media screen and (min-width: ".concat(n, ")");
});
var opacity = {
  disabled: 0.7
};
var radii = {
  none: 0,
  "default": '4px',
  rounded: '1000px'
};
var shadows = {
  "default": '0 1px 1px 0 rgba(0, 0, 0, 0.1)',
  primary: '0 1px 2px 0 rgba(0, 0, 0, 0.2)',
  heavy: '0 2px 2px 0 rgba(0, 0, 0, 0.1)',
  focus: "0 0 2px 2px ".concat(colors.greys.dusty, ";"),
  small: '0px 1px 2px rgba(0, 0, 0, 0.15)',
  mid: '0px 2px 6px rgba(0, 0, 0, 0.15)',
  large: '0px 2px 20px rgba(0, 0, 0, 0.15)',
  xLargeSoft: '0px 15px 20px rgba(0, 0, 0, 0.02)',
  none: 0
};
var space = range(0, 129, 4).map(function (i) {
  return rem("".concat(i, "px"));
});
var stripe = {
  "default": {
    color: colors.greys.charcoal
  },
  primary: {
    color: colors.brand.primary
  }
};
var textStyles = {
  caps: {
    textTransform: 'uppercase',
    fontWeight: fontWeights.bold
  },
  h1: {
    fontSize: fontSizes['3xl'],
    fontWeight: fontWeights.bold,
    lineHeight: lineHeights.normal
  },
  h2: {
    fontSize: fontSizes['2xl'],
    fontWeight: fontWeights.bold,
    lineHeight: lineHeights.normal
  },
  h3: {
    fontSize: fontSizes.xl,
    fontWeight: fontWeights.bold,
    lineHeight: lineHeights.loose
  },
  h4: {
    fontSize: fontSizes.lg,
    fontWeight: fontWeights.bold,
    lineHeight: lineHeights.loose
  },
  h5: {
    fontSize: fontSizes.md,
    fontWeight: fontWeights.bold,
    lineHeight: lineHeights.loose
  },
  h6: {
    fontSize: fontSizes.sm,
    fontWeight: fontWeights.bold,
    lineHeight: lineHeights.loose
  },
  label: {
    fontSize: fontSizes.sm,
    lineHeight: lineHeights.loose,
    fontWeight: fontWeights.bold,
    color: colors.greys.charcoal
  },
  text: {
    fontSize: fontSizes.base,
    lineHeight: lineHeights.loose,
    fontWeight: fontWeights.normal
  }
};
var transitions = {
  "default": '200ms ease-in'
};
var dividers = {
  vertical: {
    display: 'inline-block',
    height: rem('16px'),
    verticalAlign: 'middle',
    width: '1px',
    backgroundColor: colors.greys.alto
  },
  horizontal: {
    height: '1px',
    backgroundColor: colors.greys.alto
  }
};
var calendarDay = {
  selectedColor: colors.blue,
  highlightedColor: colors.bayBlue15,
  hoverColor: colors.bayBlue65
};
var radio = {
  checkedBorderColor: brand.secondary,
  checkedColor: greys.charcoal
};
var tooltip = {
  light: {
    color: colors.greys.charcoal,
    backgroundColor: colors.bayBlue30
  },
  dark: {
    color: colors.white,
    backgroundColor: colors.greys.charcoal
  },
  white: {
    color: colors.greys.charcoal,
    backgroundColor: colors.white
  }
};
export default {
  alertStyles: alertStyles,
  borders: borders,
  breakpoints: breakpoints,
  buttons: buttons,
  buttonGroup: buttonGroup,
  colors: colors,
  dividers: dividers,
  errorMessage: errorMessage,
  fontFamily: fontFamily,
  fontSizes: fontSizes,
  fontWeights: fontWeights,
  gutters: gutters,
  icons: icons,
  inputStyles: inputStyles,
  letterSpacings: letterSpacings,
  lineHeights: lineHeights,
  maxWidths: maxWidths,
  mediaCounter: mediaCounter,
  mediaQueries: mediaQueries,
  opacity: opacity,
  radii: radii,
  radio: radio,
  shadows: shadows,
  space: space,
  stripe: stripe,
  textStyles: textStyles,
  transitions: transitions,
  calendarDay: calendarDay,
  tooltip: tooltip
};